import { useState } from 'react';

import type { WorkoutSessionDetails } from '../../application/dto/WorkoutSessionDetails';
import { fromExerciseLogData } from '../../domain/workout/WorkoutContent';
import { ExerciseStatus, WorkoutSessionStatus } from '../../domain/workout/enums';
import type { ExerciseLog } from '../../domain/workout/ExerciseLog';
import { Button } from './Button';
import { DumbbellIcon } from '../icons';
import { resolveImagery } from '../design-system/data/imagery';
import { useTranslation } from '../design-system/data/translations';

/** Pack V2 (1870×841) — remplace le bandeau vectoriel `sport.svg` ; celui-ci reste défini en CSS
 * pour rollback (voir revue visuelle « intégration finale des 20 visuels »). */
const CARD_IMAGE = resolveImagery('card-sport-v2.png');
/* Nouvelle source (revue « remplacement image Sport + typo navigation ») : le sportif est
   désormais cadré beaucoup plus loin dans l'image, entièrement visible (tête aux pieds). L'ancien
   `75% 5%` compensait un cadrage serré propre à l'ancienne photo et n'a plus lieu d'être — il
   coupait la tête/le haut du corps sur la nouvelle source. Le conteneur média applique déjà
   `aspect-ratio: 1870 / 841` (= ratio natif du fichier), donc `cover` n'a normalement rien à
   recadrer ; centre neutre en attente de validation visuelle. */
const CARD_IMAGE_POSITION = '50% 50%';

interface WorkoutSessionPanelProps {
  /** `undefined` : pas de séance active aujourd'hui — voir `loading` pour distinguer du chargement. */
  readonly details: WorkoutSessionDetails | undefined;
  /** Une séance est en cours mais ses détails sont encore en cours de récupération. */
  readonly loading?: boolean;
  readonly busy: boolean;
  readonly onComplete: (exerciseId: ExerciseLog['id'], weight?: number) => void;
  readonly onSkip: (exerciseId: ExerciseLog['id']) => void;
  readonly onReplace: (exerciseId: ExerciseLog['id']) => void;
  readonly onReduce: () => void;
}

const CARD_BANNER = (
  <div className="thematic-banner thematic-banner--photo" aria-hidden="true">
    <img
      src={CARD_IMAGE}
      alt=""
      className="thematic-banner__image"
      style={{ objectPosition: CARD_IMAGE_POSITION }}
    />
    <div className="thematic-banner__veil" />
  </div>
);

/**
 * DDB-011 (Option A, arbitrated 2026-08-04): the mission-level simplification already applied
 * to daily missions extends by default to sport session content. Only the current pending
 * exercise exposes actions; the rest of the list stays a compact, inert checklist so the
 * session never presents more simultaneous choices than the one currently being executed.
 *
 * L'emplacement Sport reste toujours rendu (voir revue visuelle V7 #6) : sans séance active, il
 * affiche un état vide factuel plutôt que de disparaître — jamais de donnée inventée.
 */
export function WorkoutSessionPanel({
  details,
  loading = false,
  busy,
  onComplete,
  onSkip,
  onReplace,
  onReduce,
}: WorkoutSessionPanelProps) {
  const [weightInput, setWeightInput] = useState('');
  const { t } = useTranslation();

  const cardHeader = (
    <p className="workout-session__header">
      <DumbbellIcon className="workout-session__header-icon" aria-hidden="true" />
      <span className="eyebrow" style={{ margin: 0 }}>
        {t('sport.label')}
      </span>
    </p>
  );

  if (!details) {
    return (
      <section className="workout-session editorial-card" aria-label={t('sport.label')}>
        {CARD_BANNER}
        <div className="editorial-card__body">
          {cardHeader}
          <p className="workout-session__empty" aria-busy={loading || undefined}>
            {loading ? t('sport.loading') : t('sport.empty')}
          </p>
        </div>
      </section>
    );
  }

  const exerciseStatusLabels: Record<string, string> = {
    [ExerciseStatus.pending]: t('sport.exercise.status.pending'),
    [ExerciseStatus.completed]: t('sport.exercise.status.completed'),
    [ExerciseStatus.skipped]: t('sport.exercise.status.skipped'),
    [ExerciseStatus.replaced]: t('sport.exercise.status.replaced'),
  };

  const sessionStatusLabels: Record<string, string> = {
    [WorkoutSessionStatus.pending]: t('sport.status.pending'),
    [WorkoutSessionStatus.inProgress]: t('sport.status.in_progress'),
    [WorkoutSessionStatus.completed]: t('sport.status.completed'),
    [WorkoutSessionStatus.reduced]: t('sport.status.reduced'),
  };

  const current = details.exercises.find((exercise) => {
    const content = fromExerciseLogData(exercise.data);
    return content?.status === ExerciseStatus.pending;
  });
  const sessionEnded =
    details.session.status === WorkoutSessionStatus.completed ||
    details.session.status === WorkoutSessionStatus.reduced;

  return (
    <section className="workout-session editorial-card" aria-label={t('sport.label')}>
      {CARD_BANNER}
      <div className="editorial-card__body">
        {cardHeader}
        <p className="workout-session__safety">{t('sport.safety')}</p>
        <p className="workout-session__status">
          {sessionStatusLabels[details.session.status] ?? details.session.status}
        </p>
        <ul className="workout-session__list">
          {details.exercises.map((exercise) => {
            const content = fromExerciseLogData(exercise.data);
            if (!content) return null;
            const isCurrent = current?.id === exercise.id;
            return (
              <li key={exercise.id} className="workout-session__item">
                <span className="workout-session__item-name">{t(`exercise.${content.catalogId}.name`)}</span>
                <span className="workout-session__item-target">
                  {content.targetValue} {content.unit === 'reps' ? t('sport.units.reps') : content.unit === 'minutes' ? t('sport.units.minutes') : t('sport.units.seconds')}
                </span>
                <span className="workout-session__item-status">
                  {exerciseStatusLabels[content.status] ?? content.status}
                </span>
                {isCurrent ? (
                  <div className="workout-session__actions">
                    <p className="workout-session__instructions">{t(`exercise.${content.catalogId}.instructions`)}</p>
                    {content.supportsWeight ? (
                      <label className="workout-session__weight">
                        {t('sport.weight_label')}
                        <input
                          type="number"
                          min={0}
                          inputMode="decimal"
                          value={weightInput}
                          onChange={(event) => setWeightInput(event.target.value)}
                          aria-label={t('sport.weight_aria').replace('{name}', t(`exercise.${content.catalogId}.name`))}
                        />
                      </label>
                    ) : null}
                    <Button
                      onClick={() => {
                        const parsed = Number(weightInput);
                        onComplete(
                          exercise.id,
                          content.supportsWeight && weightInput !== '' && Number.isFinite(parsed)
                            ? parsed
                            : undefined,
                        );
                        setWeightInput('');
                      }}
                      disabled={busy}
                      aria-label={t('sport.complete_aria').replace('{name}', t(`exercise.${content.catalogId}.name`))}
                    >
                      {t('sport.exercise.complete')}
                    </Button>
                    {content.replacementCatalogId ? (
                      <Button
                        variant="quiet"
                        onClick={() => onReplace(exercise.id)}
                        disabled={busy}
                        aria-label={t('sport.replace_aria').replace('{name}', t(`exercise.${content.catalogId}.name`))}
                      >
                        {t('sport.exercise.replace')}
                      </Button>
                    ) : null}
                    <Button
                      variant="quiet"
                      onClick={() => onSkip(exercise.id)}
                      disabled={busy}
                      aria-label={t('sport.skip_aria').replace('{name}', t(`exercise.${content.catalogId}.name`))}
                    >
                      {t('sport.exercise.skip')}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        {!sessionEnded ? (
          <Button
            variant="quiet"
            onClick={onReduce}
            disabled={busy}
            aria-label={t('sport.reduce_aria')}
          >
            {t('sport.reduce')}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
